import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Paper,
  Divider,
  Alert,
  Fade,
  Container,
  Link,
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Login as LoginIcon
} from '@mui/icons-material';
import Logo from '../../components/common/Logo';

const Login = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallMobile = useMediaQuery('(max-width:350px)');
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Simple validation
    if (!credentials.email || !credentials.password) {
      setError('Veuillez remplir tous les champs');
      setLoading(false);
      return;
    }

    // Simulate authentication (in a real app, this would be an API call)
    setTimeout(() => {
      // Check for stored credentials or use default demo credentials
      const storedCredentials = localStorage.getItem('loginCredentials');
      const defaultEmail = 'admin@travelit.dz';
      const defaultPassword = 'admin123';
      
      const validEmail = storedCredentials ? JSON.parse(storedCredentials).email : defaultEmail;
      const validPassword = storedCredentials ? JSON.parse(storedCredentials).password : defaultPassword;
      
      if (credentials.email === validEmail && credentials.password === validPassword) {
        // Store authentication state
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', JSON.stringify({
          name: 'Ahmed',
          role: 'Admin',
          email: credentials.email
        }));
        
        // Redirect to dashboard
        navigate('/tableau-de-bord');
      } else {
        setError('Email ou mot de passe incorrect');
      }
      setLoading(false);
    }, 1000);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        py: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 3, md: 4 }
      }}
    >
      <Container maxWidth="md" sx={{ width: '100%' }}>
        <Card 
          elevation={10}
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            width: '100%'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Left side - Brand and Info */}
            <Box 
              sx={{ 
                flex: '1',
                bgcolor: '#f8fafc',
                p: { xs: 3, sm: 3.5, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                // Hide on very small screens to focus on login form
                display: { xs: 'none', sm: 'flex' }
              }}
            >
              <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
                <Logo size="large" variant="default" />
              </Box>

              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#334155' }}>
                Système de Gestion de Voyage
              </Typography>

              <Typography variant="body1" sx={{ mb: 3, color: '#64748b' }}>
                Bienvenue sur la plateforme TravelIT, votre solution complète pour la gestion 
                d'agence de voyage. Gérez vos clients, réservations, forfaits et finances 
                en un seul endroit.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#334155' }}>
                  Fonctionnalités principales:
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: '#64748b' }}>
                    • Gestion complète des clients et réservations
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: '#64748b' }}>
                    • Suivi financier et rapports détaillés
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: '#64748b' }}>
                    • Tableau de bord en temps réel
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: '#64748b' }}>
                    • Gestion des forfaits et destinations
                  </Typography>
                </Stack>
              </Box>

              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                © 2024 TravelIT. Tous droits réservés.
              </Typography>
            </Box>

            {/* Right side - Login Form */}
            <Box 
              sx={{ 
                flex: '1',
                p: { xs: 3, sm: 3.5, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                position: 'relative',
                // Add branding on small screens since left panel is hidden
                '&::before': {
                  content: {
                    xs: '""',
                    sm: 'none'
                  },
                  backgroundImage: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  height: '8px',
                  width: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0
                }
              }}
            >
              {/* Show logo on mobile */}
              <Box 
                sx={{ 
                  display: { xs: 'flex', sm: 'none' },
                  justifyContent: 'center',
                  mb: 3
                }}
              >
                <Logo size={isSmallMobile ? "medium" : "large"} variant="default" />
              </Box>
              
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 1, 
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}
              >
                Connexion
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: { xs: 3, md: 4 }, 
                  color: 'text.secondary',
                  textAlign: { xs: 'center', sm: 'left' }
                }}
              >
                Entrez vos identifiants pour accéder au système
              </Typography>

              {error && (
                <Fade in={!!error}>
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                </Fade>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Email"
                  name="email"
                  type="email"
                  value={credentials.email}
                  onChange={handleChange}
                  placeholder="admin@travelit.dz"
                  autoComplete="email"
                  inputProps={{
                    autoCapitalize: "none",
                    autoCorrect: "off"
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                    sx: {
                      height: { xs: '52px', md: '56px' }, // Taller input on mobile
                      fontSize: '16px', // Prevent zoom on iOS
                      borderRadius: { xs: '10px', md: '12px' }
                    }
                  }}
                  sx={{ 
                    mb: { xs: 2.5, md: 2 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: { xs: '10px', md: '12px' }
                    }
                  }}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Mot de passe"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                          sx={{ 
                            padding: { xs: '10px', md: '8px' } // Larger touch target
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      height: { xs: '52px', md: '56px' }, // Taller input on mobile
                      fontSize: '16px', // Prevent zoom on iOS
                      borderRadius: { xs: '10px', md: '12px' }
                    }
                  }}
                  sx={{ 
                    mb: { xs: 3.5, md: 3 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: { xs: '10px', md: '12px' }
                    }
                  }}
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={<LoginIcon />}
                  sx={{ 
                    py: { xs: 1.75, md: 1.5 },
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: { xs: '10px', md: '12px' },
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                    fontSize: { xs: '1rem', md: '1rem' },
                    minHeight: { xs: '52px', md: '52px' }, // Ensure good touch target
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    }
                  }}
                >
                  {loading ? 'Connexion en cours...' : 'Se connecter'}
                </Button>
              </form>

              <Box sx={{ mt: { xs: 4, md: 3 }, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary',
                  fontSize: { xs: '0.75rem', md: '0.8rem' },
                  fontWeight: 500
                }}>
                  Identifiants de démonstration:
                </Typography>
                <Box sx={{ 
                  mt: 1.5, 
                  p: { xs: 2, sm: 2.5, md: 3 }, 
                  bgcolor: '#f8fafc', 
                  borderRadius: { xs: 2, md: 3 },
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
                }}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'center',
                    alignItems: { xs: 'center', sm: 'center' },
                    gap: { xs: 1.5, sm: 3 }
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ 
                        color: 'text.secondary',
                        fontWeight: 600,
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        mb: 0.5,
                        display: 'block'
                      }}>
                        EMAIL
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontSize: { xs: '0.875rem', md: '0.9375rem' },
                        fontWeight: 500
                      }}>
                        admin@travelit.dz
                      </Typography>
                    </Box>
                    
                    <Box sx={{ 
                      height: { xs: 1, sm: 40 }, 
                      width: { xs: 80, sm: 1 }, 
                      bgcolor: 'rgba(226, 232, 240, 0.8)',
                      my: { xs: 1, sm: 0 }
                    }} />
                    
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ 
                        color: 'text.secondary',
                        fontWeight: 600,
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        mb: 0.5,
                        display: 'block'
                      }}>
                        MOT DE PASSE
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontSize: { xs: '0.875rem', md: '0.9375rem' },
                        fontWeight: 500
                      }}>
                        admin123
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mt: { xs: 4, md: 3 }, textAlign: 'center' }}>
                <Link 
                  href="#" 
                  variant="body2" 
                  sx={{ 
                    color: 'primary.main',
                    fontSize: { xs: '0.875rem', md: '0.9375rem' },
                    py: { xs: 1.5, md: 1 }, // Larger touch target on mobile
                    px: { xs: 2, md: 1 },
                    display: 'inline-block',
                    fontWeight: 500
                  }}
                >
                  Mot de passe oublié?
                </Link>
              </Box>
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;